import React from "react";

import { cn } from "./cn";
import { CircleIcon, MinusCircleIcon } from "./icons";

/**
 * Rolled-up state of one card, worn as a badge in its corner.
 *
 * The detail lives inside the card — the panels say which rows, the grid says
 * which figure — so the badge only has to answer "does this need me".
 *
 * Extracted from the opening-balances step when the customers & vendors step
 * grew the same upload-panels-in-a-card shape.
 */
export type CardStatus = "not-started" | "uploaded" | "needs-attention" | "skipped";

const STATUS_LABEL: Record<CardStatus, string> = {
  "not-started": "Not started",
  uploaded: "Uploaded",
  "needs-attention": "Needs attention",
  skipped: "Skipped",
};

const STATUS_STYLE: Record<CardStatus, string> = {
  "not-started": "bg-muted text-muted-foreground",
  uploaded: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  "needs-attention": "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  skipped: "bg-muted text-muted-foreground/70",
};

export function StatusBadge({ status }: { status: CardStatus }) {
  const Icon = status === "skipped" ? MinusCircleIcon : CircleIcon;
  return (
    <span
      className={cn(
        "flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
        STATUS_STYLE[status]
      )}
    >
      <Icon className="size-3.5" />
      {STATUS_LABEL[status]}
    </span>
  );
}

export function StatusCard({
  title,
  description,
  status,
  children,
  className,
}: {
  title: string;
  description?: string;
  status?: CardStatus;
  children: React.ReactNode;
  /** For the rare card that has to escape the page's shared width. */
  className?: string;
}) {
  return (
    <section className={cn("flex flex-col gap-4 rounded-lg border p-5", className)}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-medium">{title}</h3>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
        {/* "Not started" earns no badge: an untouched card already looks untouched,
            and grey text saying so is one more thing to read. */}
        {status && status !== "not-started" && <StatusBadge status={status} />}
      </div>
      {children}
    </section>
  );
}
